import { QAReasonCard } from "@/components/cards/dfp-submission-qa-card";
import { CopyableCellRenderer } from "@/components/tables/renderers/copyable-cell-renderer";
import { DB_ORG } from "@/constants/db";
import type {
	DynamicSubmissionDetailAction,
	DynamicSubmissionDetailProps,
	DynamicSubmissionDetailState,
	Question,
} from "@/models/dynamic-submission/dynamic-submission-detail/DyanmicSubmissionDetail.model";
import { exportSubmissionToCSV } from "@/services/export-service";
import { getSQLForm } from "@/services/form-service";
import { selfOrganizationEquipmentAreas } from "@/services/organization-service";
import { getSQLSubmissionWithAsset } from "@/services/submission-service";
import { getOrganizationAssetTypes } from "@/services/utility-service";
import { useUserStore } from "@/stores/user-store";
import { normalizeStringForComparison } from "@/utils/transforms";
import { isValidUrl } from "@/utils/utility-fns";
import {
	Document,
	Image as PDFImage,
	Page,
	StyleSheet,
	Text,
	View,
	pdf,
} from "@react-pdf/renderer";
import { Card, Descriptions, Image, Spin, Typography, message } from "antd";
import dayjs from "dayjs";
import html2pdf from "html2pdf.js";
import {
	type FC,
	type ReactNode,
	useEffect,
	useReducer,
	useState,
} from "react";
import { LuXCircle } from "react-icons/lu";
import { useLocation, useNavigate } from "react-router-dom";
import {
	type SubmissionData,
	conditionMap,
} from "./DynamicSubmissionDetailAssetRenderer";
import { SubmissionDetailsHeader } from "./DynamicSubmissionDetailHeader";
import { EditLog } from "./EditLog";

const { Title } = Typography;

function DynamicSubmissionDetailsReducer(
	state: DynamicSubmissionDetailState,
	action: DynamicSubmissionDetailAction,
): DynamicSubmissionDetailState {
	switch (action.type) {
		case "SET_LOADING":
			return { ...state, isLoading: action.payload };
		case "SET_STATE":
			return { ...state, ...action.payload };
		default:
			return state;
	}
}

// initial state for the reducer
const SubmissionDetailsInitialState: DynamicSubmissionDetailState = {
	isLoading: true,
	submissionData: {},
	formName: "",
	formLogo: "",
	dateOfVisit: "",
	address: "",
	pdfLoading: false,
	locationId: "",
	isSubmissionCompleted: false,
	isDeleted: false,
	data: {},
};

/* ======================================================================================
   Subcomponent: SubmissionContent
   Renders the "submission-content" area (logo, title, QAReasonCard, and either
   the form sections or the dynamic asset details).
   ====================================================================================== */
export interface DynamicSubmissionDetailsContentProps {
	state: DynamicSubmissionDetailState;
	userOrgId: string;
	assetTypesMap?: Map<number | string, string>;
	equipmentAreasMap?: Map<number | string, string>;
}

/**
 * Renders a single question's value (common logic),
 * skipping any empty or invalid data.
 */
function renderDynamicSubmissionDetailsQuestionValue(
	question: Question,
	state: DynamicSubmissionDetailState,
	userOrgId: string,
	assetTypesMap?: Map<number | string, string>,
	equipmentAreasMap?: Map<number | string, string>,
): ReactNode {
	const { address } = state;

	// For standard org vs. Cushman, we call different logic
	if (userOrgId === DB_ORG.CUSHMANWAKEFIELD) {
		return renderQuestionCushmanValue(
			question,
			state,
			userOrgId,
			assetTypesMap,
			equipmentAreasMap,
		);
	}

	const val = question?.value;
	if (
		val === undefined ||
		val === null ||
		val === "" ||
		(Array.isArray(val) && val.length === 0)
	) {
		return null;
	}

	if (
		(question.type === "group" || question.type === "dynamicList") &&
		Array.isArray(val) &&
		question.fields
	) {
		// Identify different data structure styles
		const firstItem = val[0];
		const isOldStyle = Array.isArray(firstItem);

		// Form IDs 2 and 4 use a special structure: array of {name, type, label, value} objects
		// Check if firstItem has 'name' property (value may or may not be present)
		// Also check if ANY item in the array has both 'name' and a non-undefined value
		const hasNameProperty =
			!isOldStyle &&
			firstItem !== null &&
			typeof firstItem === "object" &&
			"name" in firstItem;
		const isNamedObjectStyle =
			hasNameProperty &&
			val.some(
				(item: any) => item && typeof item === "object" && "name" in item,
			);

		const isNewStyle =
			!isOldStyle &&
			!isNamedObjectStyle &&
			firstItem !== null &&
			typeof firstItem === "object" &&
			!("fields" in firstItem);
		const isOtherStyle =
			!isOldStyle &&
			!isNamedObjectStyle &&
			firstItem !== null &&
			typeof firstItem === "object" &&
			"fields" in firstItem &&
			Array.isArray(firstItem.fields);

		// ----- NAMED OBJECT STYLE (Form IDs 2 and 4) => array of {name, type, label, value} objects
		if (isNamedObjectStyle) {
			// Convert the array of {name, value} objects to a map, similar to old style
			const subMap: Record<string, unknown> = {};

			val.forEach((el: any) => {
				if (el && typeof el === "object" && el.name) {
					subMap[el.name] = el.value;
				}
			});

			const nestedFields: ReactNode[] = [];
			question.fields!.forEach((sf) => {
				const subKey = sf.name || sf.key || sf.responseTag;
				const subVal = subMap[subKey || ""];
				const childNode = renderDynamicSubmissionDetailsQuestionValue(
					{ ...sf, value: subVal },
					state,
					userOrgId,
					assetTypesMap,
					equipmentAreasMap,
				);
				if (
					childNode !== null &&
					!(Array.isArray(childNode) && childNode.length === 0)
				) {
					nestedFields.push(
						<Descriptions.Item
							key={`${question.name || question.key}-${subKey}`}
							label={sf.label || subKey}
						>
							{childNode}
						</Descriptions.Item>,
					);
				}
			});

			if (nestedFields.length === 0) return null;

			return (
				<Descriptions
					key={`named-object-${question.name || question.key}`}
					bordered
					column={1}
					labelStyle={{ fontWeight: "bold", width: "30%" }}
					className="mb-4"
				>
					{nestedFields}
				</Descriptions>
			);
		}

		// ----- OLD STYLE => array-of-arrays
		if (isOldStyle) {
			const oldStyleItems: ReactNode[] = [];
			val.forEach((subArr: Record<string, string>[], idx: number) => {
				if (!Array.isArray(subArr)) return;
				const subMap: Record<string, unknown> = {};
				subArr.forEach((el) => {
					if (el && typeof el === "object" && el.name) {
						subMap[el.name] = el.value;
					}
				});

				const nestedFields: ReactNode[] = [];
				question.fields!.forEach((sf) => {
					const subKey = sf.name || sf.key || sf.responseTag;
					const subVal = subMap[subKey || ""];
					const childNode = renderDynamicSubmissionDetailsQuestionValue(
						{ ...sf, value: subVal },
						state,
						userOrgId,
						assetTypesMap,
						equipmentAreasMap,
					);
					if (
						childNode !== null &&
						!(Array.isArray(childNode) && childNode.length === 0)
					) {
						nestedFields.push(
							<Descriptions.Item
								key={`${idx}-${subKey}`}
								label={sf.label || subKey}
							>
								{childNode}
							</Descriptions.Item>,
						);
					}
				});

				if (nestedFields.length > 0) {
					oldStyleItems.push(
						<Descriptions
							key={`old-style-${idx}-${question.name || question.key}`}
							bordered
							column={1}
							labelStyle={{ fontWeight: "bold", width: "30%" }}
							className="mb-4"
						>
							{nestedFields}
						</Descriptions>,
					);
				}
			});

			if (oldStyleItems.length === 0) return null;
			return <>{oldStyleItems}</>;
		}

		// ----- NEW STYLE => array-of-plain-objects
		if (isNewStyle) {
			const newStyleItems: ReactNode[] = [];
			val.forEach((obj: Record<string, unknown>, idx: number) => {
				if (!obj || typeof obj !== "object") return;
				const nestedFields: ReactNode[] = [];
				question.fields!.forEach((sf) => {
					const subKey = sf.name || sf.key || sf.responseTag;
					const subVal = obj[subKey as keyof typeof obj];
					const childNode = renderDynamicSubmissionDetailsQuestionValue(
						{ ...sf, value: subVal },
						state,
						userOrgId,
						assetTypesMap,
						equipmentAreasMap,
					);
					if (
						childNode !== null &&
						!(Array.isArray(childNode) && childNode.length === 0)
					) {
						nestedFields.push(
							<Descriptions.Item
								key={`${idx}-${subKey}`}
								label={sf.label || subKey}
							>
								{childNode}
							</Descriptions.Item>,
						);
					}
				});

				if (nestedFields.length > 0) {
					newStyleItems.push(
						<Descriptions
							key={`new-style-${idx}-${question.name || question.key}`}
							bordered
							column={1}
							labelStyle={{ fontWeight: "bold", width: "30%" }}
							className="mb-4"
						>
							{nestedFields}
						</Descriptions>,
					);
				}
			});

			if (newStyleItems.length === 0) return null;
			return <>{newStyleItems}</>;
		}

		// ----- OTHER STYLE => each item has .fields
		if (isOtherStyle) {
			const otherStyleItems: ReactNode[] = [];
			val.forEach((item: Record<string, any>, idx: number) => {
				if (!item || !Array.isArray(item.fields)) return;
				const nestedFields: ReactNode[] = [];
				question.fields!.forEach((sf) => {
					const subObj = item.fields.find(
						(f: Record<string, string>) => f.name === sf.name,
					);
					const subVal = subObj?.value;
					const childNode = renderDynamicSubmissionDetailsQuestionValue(
						{ ...sf, value: subVal },
						state,
						userOrgId,
						assetTypesMap,
						equipmentAreasMap,
					);
					if (
						childNode !== null &&
						!(Array.isArray(childNode) && childNode.length === 0)
					) {
						nestedFields.push(
							<Descriptions.Item
								key={`${idx}-${sf.name}`}
								label={sf.label || sf.name}
							>
								{childNode}
							</Descriptions.Item>,
						);
					}
				});

				if (nestedFields.length > 0) {
					otherStyleItems.push(
						<Descriptions
							key={`other-style-${idx}-${question.name || question.key}`}
							bordered
							column={1}
							labelStyle={{ fontWeight: "bold", width: "30%" }}
							className="mb-4"
						>
							{nestedFields}
						</Descriptions>,
					);
				}
			});

			if (otherStyleItems.length === 0) return null;
			return <>{otherStyleItems}</>;
		}

		// if none matched, skip
		return null;
	}

	// IMAGE
	if (question.type === "image" || question.type === "file") {
		const images = Array.isArray(val) ? val : [val];
		const valid = images.filter(isValidUrl);
		if (valid.length === 0) return null;
		return valid.map((url, i) => (
			<Image
				key={`${question.name || question.key}-img-${i}`}
				src={url}
				alt={question.label || "image"}
				style={{ maxWidth: 250 }}
				preview={{ destroyOnClose: true }}
			/>
		));
	}

	if (question.type === "select" && question.name === "manufacturer") {
		// Try multiple sources for manufacturer name (from joined data)
		let manufacturerName: string | undefined;

		// Check nested manufacturer path
		if (state?.data?.asset?.model?.manufacturer?.name) {
			manufacturerName = state.data.asset.model.manufacturer.name;
		} else if (state?.data?.manufacturer_name) {
			manufacturerName = state.data.manufacturer_name;
		} else if (state?.data?.asset?.manufacturer) {
			const assetManufacturer = state.data.asset.manufacturer;
			if (typeof assetManufacturer === "string") {
				manufacturerName = assetManufacturer;
			} else if (
				assetManufacturer &&
				typeof assetManufacturer === "object" &&
				"name" in assetManufacturer
			) {
				manufacturerName = (assetManufacturer as { name: string }).name;
			}
		} else if (state?.data?.manufacturer) {
			const manufacturer = state.data.manufacturer;
			if (typeof manufacturer === "string") {
				manufacturerName = manufacturer;
			} else if (
				manufacturer &&
				typeof manufacturer === "object" &&
				"name" in manufacturer
			) {
				manufacturerName = (manufacturer as { name: string }).name;
			}
		}

		if (manufacturerName) {
			return <CopyableCellRenderer value={manufacturerName} />;
		}
		return <CopyableCellRenderer value={question.value} />;
	}

	if (question.type === "select" && question.name === "assetType") {
		// Get asset type name from joined data (if asset exists)
		// The backend returns asset_type as an object with id and name
		const assetTypeObj = state?.data?.asset_type;
		let assetTypeName: string | undefined;

		if (
			assetTypeObj &&
			typeof assetTypeObj === "object" &&
			"name" in assetTypeObj
		) {
			assetTypeName = assetTypeObj.name;
		} else if (state?.data?.asset_type_name) {
			// Fallback to flat field if object not available
			assetTypeName = state.data.asset_type_name;
		}

		// If still no name, try to resolve from question.value, question.options, or submissionData
		if (!assetTypeName && question.value) {
			// question.value might be an ID, or an object with id/name
			if (typeof question.value === "object" && question.value !== null) {
				if ("name" in question.value) {
					assetTypeName = String(question.value.name);
				} else if ("label" in question.value) {
					assetTypeName = String(question.value.label);
				} else if ("id" in question.value) {
					// Try to find name from question.options
					const option = question.options?.find(
						(opt: any) =>
							opt.value === question.value.id || opt.id === question.value.id,
					);
					if (option) {
						assetTypeName = option.label || option.name || String(option.value);
					}
				}
			} else if (
				typeof question.value === "number" ||
				typeof question.value === "string"
			) {
				// Value is an ID - try to find name from question.options first
				if (question.options && Array.isArray(question.options)) {
					const option = question.options.find(
						(opt: any) =>
							opt.value === question.value ||
							opt.id === question.value ||
							opt.value === String(question.value),
					);
					if (option) {
						assetTypeName = option.label || option.name || String(option.value);
					}
				}

				// If not found in options, try to look up from assetTypesMap
				if (!assetTypeName && assetTypesMap) {
					const mappedName =
						assetTypesMap.get(question.value) ||
						assetTypesMap.get(String(question.value));
					if (mappedName) {
						assetTypeName = mappedName;
					}
				}

				// If still not found, try to find name from submissionData (parsed raw_input)
				if (!assetTypeName) {
					const submissionData = state?.submissionData;
					if (submissionData && typeof submissionData === "object") {
						// Check multiple possible locations for assetType
						const rawAssetType =
							(submissionData as any).assetType ||
							(submissionData as any).asset_type ||
							(submissionData as any).assetTypeId;

						if (rawAssetType) {
							if (typeof rawAssetType === "object" && rawAssetType !== null) {
								if ("name" in rawAssetType) {
									assetTypeName = String(rawAssetType.name);
								} else if ("label" in rawAssetType) {
									assetTypeName = String(rawAssetType.label);
								}
							} else if (typeof rawAssetType === "string") {
								// Might be a name string directly
								assetTypeName = rawAssetType;
							}
						}
					}
				}
			}
		}

		if (assetTypeName) {
			return <CopyableCellRenderer value={assetTypeName} />;
		}
		// Fallback: show the value if it's a string (might be a name)
		if (typeof question.value === "string" && question.value !== "") {
			return <CopyableCellRenderer value={question.value} />;
		}
		// Don't show IDs - return null or "Not available"
		return <CopyableCellRenderer value="Not available" />;
	}

	if (question.type === "select" && question.name === "assetLocation") {
		// Get equipment area name from joined data (if asset exists)
		// The backend returns equipment_area as an object with id and name
		const equipmentAreaObj = state?.data?.equipment_area;
		let locationName: string | undefined;

		if (
			equipmentAreaObj &&
			typeof equipmentAreaObj === "object" &&
			"name" in equipmentAreaObj
		) {
			locationName = equipmentAreaObj.name;
		} else if (state?.data?.equipment_area_name) {
			// Fallback to flat field if object not available
			locationName = state.data.equipment_area_name;
		}

		// If still no name, try to resolve from question.value, question.options, or submissionData
		if (!locationName && question.value) {
			// question.value might be an ID, or an object with id/name
			if (typeof question.value === "object" && question.value !== null) {
				if ("name" in question.value) {
					locationName = String(question.value.name);
				} else if ("label" in question.value) {
					locationName = String(question.value.label);
				} else if ("id" in question.value) {
					// Try to find name from question.options
					const option = question.options?.find(
						(opt: any) =>
							opt.value === question.value.id || opt.id === question.value.id,
					);
					if (option) {
						locationName = option.label || option.name || String(option.value);
					}
					// Also try lookup map
					if (!locationName && equipmentAreasMap) {
						const mappedName =
							equipmentAreasMap.get(question.value.id) ||
							equipmentAreasMap.get(String(question.value.id));
						if (mappedName) {
							locationName = mappedName;
						}
					}
				}
			} else if (
				typeof question.value === "number" ||
				typeof question.value === "string"
			) {
				// Value is an ID - try to find name from question.options first
				if (question.options && Array.isArray(question.options)) {
					const option = question.options.find(
						(opt: any) =>
							opt.value === question.value ||
							opt.id === question.value ||
							opt.value === String(question.value),
					);
					if (option) {
						locationName = option.label || option.name || String(option.value);
					}
				}

				// If not found in options, try to look up from equipmentAreasMap
				if (!locationName && equipmentAreasMap) {
					const mappedName =
						equipmentAreasMap.get(question.value) ||
						equipmentAreasMap.get(String(question.value));
					if (mappedName) {
						locationName = mappedName;
					}
				}

				// If still not found, try to find name from submissionData (parsed raw_input)
				if (!locationName) {
					const submissionData = state?.submissionData;
					if (submissionData && typeof submissionData === "object") {
						// Check multiple possible locations for assetLocation
						const rawAssetLocation =
							(submissionData as any).assetLocation ||
							(submissionData as any).asset_location ||
							(submissionData as any).equipmentArea ||
							(submissionData as any).equipment_area;

						if (rawAssetLocation) {
							if (
								typeof rawAssetLocation === "object" &&
								rawAssetLocation !== null
							) {
								if ("name" in rawAssetLocation) {
									locationName = String(rawAssetLocation.name);
								} else if ("label" in rawAssetLocation) {
									locationName = String(rawAssetLocation.label);
								}
							} else if (typeof rawAssetLocation === "string") {
								// Might be a name string directly
								locationName = rawAssetLocation;
							}
						}
					}
				}
			}
		}

		if (locationName) {
			return <CopyableCellRenderer value={locationName} />;
		}
		// Fallback: show the value if it's a string (might be a name)
		if (typeof question.value === "string" && question.value !== "") {
			return <CopyableCellRenderer value={question.value} />;
		}
		// Don't show IDs - return null or "Not available"
		return <CopyableCellRenderer value="Not available" />;
	}

	if (question.type === "rating" && question.name === "conditionRating") {
		const conditionRating = question.value || state?.data?.asset?.condition;
		const conditionRatingName = conditionMap[conditionRating];
		return (
			<CopyableCellRenderer value={conditionRatingName || question.value} />
		);
	}

	// DATE
	if (question.type === "date") {
		const dt = dayjs(val);
		const dateStr = dt.isValid() ? dt.format("MM/DD/YYYY") : String(val);
		return <CopyableCellRenderer value={dateStr} />;
	}

	// LOCATION => "location_id" case => use state's address or location name
	if (
		question.type === "location" &&
		(question.name === "location_id" || question.key === "location_id")
	) {
		// Try to get location name from state data, fallback to address
		const locationName = state?.data?.location?.name || address;
		return <CopyableCellRenderer value={locationName || question.value} />;
	}

	// LOCATION => fallback
	if (question.type === "location") {
		return (
			<CopyableCellRenderer
				value={
					typeof question.value === "string"
						? question.value
						: JSON.stringify(val)
				}
			/>
		);
	}

	// BOOLEAN
	if (typeof val === "boolean") {
		return <CopyableCellRenderer value={val ? "Yes" : "No"} />;
	}

	// ARRAY
	if (Array.isArray(val)) {
		if (val.every((x) => typeof x === "string" || typeof x === "number")) {
			return <CopyableCellRenderer value={val.join(", ")} />;
		}
		return <CopyableCellRenderer value={JSON.stringify(val)} />;
	}

	// OBJECT => fallback to JSON
	if (typeof val === "object") {
		return <CopyableCellRenderer value={JSON.stringify(val)} />;
	}

	// If there's a format
	if (question.format) {
		let strVal = String(val);
		try {
			if (question.format.formatFn) {
				const formatFunc = eval(`(${question.format.formatFn})`);
				if (typeof formatFunc === "function") {
					strVal = formatFunc(strVal);
				}
			}
			if (question.format.inputSuffix) {
				strVal += ` ${question.format.inputSuffix}`;
			}
		} catch (err) {
			console.warn("Error applying formatFn:", err);
		}
		return <CopyableCellRenderer value={strVal} />;
	}

	// Fallback => raw string
	return <CopyableCellRenderer value={String(val)} />;
}

/**
 * Renders a single question's value (Cushman-specific).
 */
function renderQuestionCushmanValue(
	question: Question,
	state: DynamicSubmissionDetailState,
	userOrgId: string,
	assetTypesMap?: Map<number | string, string>,
	equipmentAreasMap?: Map<number | string, string>,
): ReactNode {
	const { address } = state;

	let val = question?.value;
	if (
		val === undefined ||
		val === null ||
		val === "" ||
		(Array.isArray(val) && val.length === 0)
	) {
		return null;
	}

	// Group/dynamicList => nested
	if (
		(question.type === "group" || question.type === "dynamicList") &&
		Array.isArray(val) &&
		question.fields
	) {
		const firstItem = val[0];
		const isOldStyle = Array.isArray(firstItem);
		const isNewStyle = !isOldStyle && typeof firstItem === "object";

		if (isOldStyle) {
			const nestedItems: ReactNode[] = [];
			val.forEach((subArr: Record<string, string>[], idx: number) => {
				if (!Array.isArray(subArr)) return;
				const subMap: Record<string, unknown> = {};
				subArr.forEach((el) => {
					if (el?.name) {
						subMap[el.name] = el.value;
					}
				});
				question.fields!.forEach((sf) => {
					const subKey = sf.name || sf.key || sf.responseTag;
					const subVal = subMap[subKey || ""];
					const childNode = renderDynamicSubmissionDetailsQuestionValue(
						{ ...sf, value: subVal },
						state,
						userOrgId,
						assetTypesMap,
						equipmentAreasMap,
					);
					if (
						childNode !== null &&
						!(Array.isArray(childNode) && childNode.length === 0)
					) {
						nestedItems.push(
							<Descriptions.Item
								key={`${idx}-${subKey}`}
								label={sf.label || subKey}
							>
								{childNode}
							</Descriptions.Item>,
						);
					}
				});
			});

			if (nestedItems.length === 0) return null;
			return (
				<Descriptions
					bordered
					column={1}
					labelStyle={{ fontWeight: "bold", width: "30%" }}
				>
					{nestedItems}
				</Descriptions>
			);
		}
		if (isNewStyle) {
			// array-of-objects
			const subMap: Record<string, unknown> = {};
			val.forEach((obj: Record<string, string>) => {
				if (obj?.name) {
					subMap[obj.name] = obj.value;
				}
			});
			const nestedItems: ReactNode[] = [];
			question.fields!.forEach((sf) => {
				const subKey = sf.name || sf.key || sf.responseTag;
				const subVal = subMap[subKey || ""];
				const childNode = renderDynamicSubmissionDetailsQuestionValue(
					{ ...sf, value: subVal },
					state,
					userOrgId,
					assetTypesMap,
					equipmentAreasMap,
				);
				if (
					childNode !== null &&
					!(Array.isArray(childNode) && childNode.length === 0)
				) {
					nestedItems.push(
						<Descriptions.Item key={subKey} label={sf.label || subKey}>
							{childNode}
						</Descriptions.Item>,
					);
				}
			});

			if (nestedItems.length === 0) return null;
			return (
				<Descriptions
					bordered
					column={1}
					labelStyle={{ fontWeight: "bold", width: "30%" }}
				>
					{nestedItems}
				</Descriptions>
			);
		}
		return null;
	}

	// IMAGE
	if (question.type === "image") {
		const images = Array.isArray(val) ? val : [val];
		const valid = images.filter(isValidUrl);
		if (valid.length === 0) return null;
		return valid.map((url, i) => (
			<Image
				key={`${question.name || question.key}-img-${i}`}
				src={url}
				alt={question.label || "image"}
				style={{ maxWidth: 250, marginRight: 8 }}
				preview={{
					destroyOnClose: true,
					closeIcon: (
						<span className="p-2 bg-white">
							<LuXCircle size="1.25rem" />
						</span>
					),
				}}
			/>
		));
	}

	// DATE
	if (question.type === "date") {
		const dt = dayjs(val);
		const str = dt.isValid() ? dt.format("MM/DD/YYYY") : String(val);
		return <CopyableCellRenderer value={str} />;
	}

	// LOCATION => "location" means display the address
	if (
		question.type === "location" &&
		(question.name === "location" || question.key === "location")
	) {
		return <CopyableCellRenderer value={address} />;
	}

	// fallback location
	if (question.type === "location") {
		return (
			<CopyableCellRenderer
				value={
					typeof question.value === "string"
						? question.value
						: JSON.stringify(val)
				}
			/>
		);
	}

	// BOOLEAN
	if (typeof val === "boolean") {
		return <CopyableCellRenderer value={val ? "Yes" : "No"} />;
	}

	// ARRAY => join if simple
	if (
		Array.isArray(val) &&
		val.every((v) => typeof v === "string" || typeof v === "number")
	) {
		return <CopyableCellRenderer value={val.join(", ")} />;
	}

	// OBJECT => JSON
	if (typeof val === "object") {
		return <CopyableCellRenderer value={JSON.stringify(val)} />;
	}

	// If there's a format
	if (question.format) {
		let valStr = String(val);
		try {
			if (question.format.formatFn) {
				const fFn = eval(`(${question.format.formatFn})`);
				if (typeof fFn === "function") {
					valStr = fFn(valStr);
				}
			}
			if (question.format.inputSuffix) {
				valStr = `${valStr} ${question.format.inputSuffix}`;
			}
		} catch (e) {
			console.warn("Cannot parse or run formatFn:", e);
		}
		return <CopyableCellRenderer value={valStr} />;
	}

	// fallback => string
	return <CopyableCellRenderer value={String(val)} />;
}

/** Renders each section + question from the state's submissionData. */
function RenderDynamicSubmissionDetailSections({
	submissionData,
	userOrgId,
	state,
	assetTypesMap,
	equipmentAreasMap,
}: any) {
	// console.log("RenderDynamicSubmissionDetailSections - submissionData:", submissionData);

	const sections =
		submissionData.sections ||
		(Array.isArray(submissionData) && submissionData[0]?.sections) ||
		[];

	const items: ReactNode[] = [];
	const renderedKeys = new Set<string>();

	sections.forEach((section: any, sIndex: number) => {
		if (!section) return;
		// console.log(`Processing section ${sIndex}:`, section);

		const fields = section.questions || section.fields || [];

		// First, collect all renderable questions for this section
		const sectionItems: ReactNode[] = [];

		fields.forEach((q: Question, qIndex: number) => {
			if (!q) return;
			const qKey = q.key || q.responseTag || q.name || `${sIndex}-${qIndex}`;
			if (renderedKeys.has(qKey)) {
				console.warn("Duplicate question key:", qKey);
				return;
			}
			renderedKeys.add(qKey);

			const contentNode = renderDynamicSubmissionDetailsQuestionValue(
				q,
				state,
				userOrgId,
				assetTypesMap,
				equipmentAreasMap,
			);

			if (
				contentNode === null ||
				(Array.isArray(contentNode) && contentNode.length === 0)
			) {
				return; // skip if empty
			}

			// console.log(`Adding question ${qKey}:`, q);
			sectionItems.push(
				<Descriptions.Item
					key={qKey}
					label={
						q.label || q.fields?.[0]?.label || q.name || `Question #${qIndex}`
					}
					span={1}
				>
					{contentNode}
				</Descriptions.Item>,
			);
		});

		// Only add section title and questions if there are renderable questions
		if (sectionItems.length > 0) {
			// Section title row
			items.push(
				<Descriptions.Item key={`section-${sIndex}-${section.title}`} span={3}>
					<Title level={4}>{section.title}</Title>
				</Descriptions.Item>,
			);

			// Add all renderable questions for this section
			items.push(...sectionItems);
		}
	});

	// console.log("RenderDynamicSubmissionDetailSections - total items:", items.length);

	// If no items, show a fallback message for debugging
	if (!items.length) {
		console.warn("No items to render in submission details", {
			sectionsCount: sections.length,
			submissionDataKeys: Object.keys(submissionData || {}),
			submissionDataSample: submissionData
				? JSON.stringify(submissionData).substring(0, 500)
				: "null",
		});
		return (
			<div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
				<p>No submission data available to display.</p>
				<p>Debug info: {sections.length} sections found</p>
				{sections.length === 0 && submissionData && (
					<details style={{ marginTop: "20px", textAlign: "left" }}>
						<summary>Raw submission data (for debugging)</summary>
						<pre
							style={{
								background: "#f5f5f5",
								padding: "10px",
								overflow: "auto",
								maxHeight: "400px",
								fontSize: "12px",
							}}
						>
							{JSON.stringify(submissionData, null, 2)}
						</pre>
					</details>
				)}
			</div>
		);
	}

	// Inject a store number field into the first section if it exists (I hate this)
	// target the first section IE the second index after address (more generally, after the first field in the first section)
	const storeNumber = state.data?.location?.store_number;
	if (storeNumber && items.length) {
		items.splice(
			2,
			0,
			<Descriptions.Item key="store-number" label="Store Number">
				<CopyableCellRenderer value={storeNumber} />
			</Descriptions.Item>,
		);
	}

	return (
		<Descriptions
			bordered
			column={1}
			labelStyle={{ fontWeight: "bold", width: "30%" }}
		>
			{items}
		</Descriptions>
	);
}

/**
 * The main "SubmissionContent" component. Checks if form_id = 3 + has .asset => show asset details,
 * else show the typical sections-based rendering. Also includes QAReasonCard + EditLog if relevant.
 */
const DynamicSubmissionDetailsContent: FC<
	DynamicSubmissionDetailsContentProps
> = ({ state, userOrgId, assetTypesMap, equipmentAreasMap }) => {
	const { formLogo, formName, submissionData, data } = state;

	return (
		<div
			id="submission-content"
			className="justify-content-center"
			style={{
				backgroundColor: "#ffffff",
				padding: "20px",
				minHeight: "100px",
				width: "100%",
				display: "block",
				position: "relative",
			}}
		>
			{/* Optional Logo */}
			{formLogo && (
				<div style={{ textAlign: "center", marginBottom: 20 }} className="mt-4">
					<img
						src={formLogo}
						alt="Form Logo"
						style={{ maxWidth: 200, maxHeight: 200 }}
					/>
				</div>
			)}

			{/* Form Title */}
			<div className="d-flex flex-row align-items-center justify-content-between w-100">
				<Title className="w-100 justify-content-center text-center" level={2}>
					{formName}
				</Title>
			</div>

			{/* QA Reason Card if flagged */}
			{data?.is_qa_flagged && data?.qa_flags?.length > 0 && (
				<QAReasonCard qaFlags={data.qa_flags} />
			)}

			<RenderDynamicSubmissionDetailSections
				submissionData={submissionData}
				userOrgId={userOrgId}
				state={state}
				assetTypesMap={assetTypesMap}
				equipmentAreasMap={equipmentAreasMap}
			/>

			{/* EditLog if any */}
			{data?.edit_history?.length > 0 && (
				<div className="mt-4 submission-edit-log">
					<EditLog editLog={data?.edit_history} />
				</div>
			)}
		</div>
	);
};

/* ======================================================================================
   Main Component: DynamicSubmissionDetail
   Uses the local reducer, fetches data, and composes subcomponents.
   ====================================================================================== */
export const DynamicSubmissionDetail: FC<DynamicSubmissionDetailProps> = (
	props,
) => {
	const {
		submissionId,
		formId,
		toggleSubmission,
		quickView = false,
		setModalIndex,
		responsesLength = 0,
	} = props;

	// Store, location, navigate
	const { user } = useUserStore();
	const location = useLocation();
	const navigate = useNavigate();

	// Our local state via reducer
	const [state, dispatch] = useReducer(
		DynamicSubmissionDetailsReducer,
		SubmissionDetailsInitialState,
	);

	const {
		isLoading,
		submissionData,
		formName,
		dateOfVisit,
		address,
		locationId,
	} = state;

	// Fetch asset types and equipment areas for name resolution
	const [assetTypesMap, setAssetTypesMap] = useState<
		Map<number | string, string>
	>(new Map());
	const [equipmentAreasMap, setEquipmentAreasMap] = useState<
		Map<number | string, string>
	>(new Map());

	// Fetch asset types and equipment areas on mount
	useEffect(() => {
		const fetchLookupData = async () => {
			try {
				// Fetch asset types - get full objects with id and name
				const assetTypes = await getOrganizationAssetTypes();
				const assetTypesMap = new Map<number | string, string>();
				assetTypes.forEach((at: any) => {
					const id = at.id || at.organizationAssetTypeId;
					const name = at.name;
					if (id && name) {
						assetTypesMap.set(id, name);
						assetTypesMap.set(String(id), name);
					}
				});
				setAssetTypesMap(assetTypesMap);

				// Fetch equipment areas
				const equipmentAreasResponse = await selfOrganizationEquipmentAreas();
				// Handle both Core API format { data: [...] } and direct array
				const equipmentAreasArray =
					equipmentAreasResponse?.data || equipmentAreasResponse || [];
				const equipmentAreasMap = new Map<number | string, string>();
				equipmentAreasArray.forEach((ea: any) => {
					// Handle different response formats
					const id = ea.id || ea.value;
					const name = ea.name || ea.label;
					if (id && name) {
						equipmentAreasMap.set(id, name);
						equipmentAreasMap.set(String(id), name);
					}
				});
				setEquipmentAreasMap(equipmentAreasMap);
			} catch (error) {
				console.error(
					"Error fetching lookup data for asset types/equipment areas:",
					error,
				);
			}
		};

		if (user?.organization?.id) {
			fetchLookupData();
		}
	}, [user?.organization?.id]);

	// Fetch on mount
	useEffect(() => {
		const fetchSubmission = async () => {
			dispatch({ type: "SET_LOADING", payload: true });

			try {
				// 1) Grab submission from DB
				const submission = await getSQLSubmissionWithAsset(submissionId, true);
				const locId = submission?.location_id;
				let rawInput: unknown = submission?.raw_input;

				// Handle double-stringified JSON
				if (typeof rawInput === "string") {
					try {
						const parsed = JSON.parse(rawInput);
						// If parsing results in another string, parse again
						if (typeof parsed === "string") {
							rawInput = JSON.parse(parsed);
						} else {
							rawInput = parsed;
						}
					} catch (e) {
						console.warn("Could not parse raw_input:", e);
					}
				}

				if (Array.isArray(rawInput) && rawInput.length > 0) {
					rawInput = rawInput[0];
				}

				let parsedData: SubmissionData = {};
				try {
					parsedData =
						typeof rawInput === "string"
							? JSON.parse(rawInput)
							: (rawInput as SubmissionData) || {};
				} catch (e) {
					console.warn("Could not parse submission raw_input as JSON:", e);
				}

				const fName = submission.form?.name || "Inspection Form";
				const isDeleted = submission.is_deleted;
				const endTime = submission.end_time;
				const isCompleted =
					endTime !== null && endTime !== undefined && endTime !== "";

				// Fetch form config to get logo and other form details
				let fLogo = user?.organization?.config?.logo_url || "";
				try {
					const formConfig = await getSQLForm(
						submission.form_id || formId,
						user?.organization?.forms,
					);
					if (formConfig?.config?.logo) {
						fLogo = formConfig.config.logo;
					}
				} catch (error) {
					console.warn("Error fetching form config for logo:", error);
					// Fallback to organization logo if form config fetch fails
				}

				// Build address
				let addr = "";
				if (submission.location?.address1) {
					addr += submission.location.address1;
				}
				if (submission.location?.address2) {
					addr += ` ${submission.location.address2}`;
				}
				if (submission.location?.city) {
					addr += `, ${submission.location.city}`;
				}
				if (submission.location?.state) {
					addr += `, ${submission.location.state}`;
				}
				if (submission.location?.zip) {
					addr += ` ${submission.location.zip}`;
				}
				addr = addr.trim();

				// Attempt to find dateOfVisit
				let dOfVisit: string | null = null;
				const sections = parsedData?.sections || [];
				for (const section of sections) {
					const qArr = section.questions || section.fields || [];
					for (const q of qArr) {
						if (
							q?.key === "dateOf_visit" ||
							q?.key === "dateOfVisit" ||
							q?.responseTag === "dateOfVisit" ||
							q?.name === "date_of_visit" ||
							q?.name === "dateOfVisit" ||
							normalizeStringForComparison(q?.label || "") ===
								normalizeStringForComparison("Date of Visit")
						) {
							dOfVisit = q.value;
							break;
						}
					}
					if (dOfVisit) break;
				}
				if (!dOfVisit) {
					dOfVisit = submission.end_time || submission.begin_time;
				}
				const dayjsDate = dayjs(dOfVisit);
				const dateValue = dayjsDate.isValid()
					? dayjsDate.format("YYYY-MM-DD")
					: (dOfVisit ?? "").toString();

				dispatch({
					type: "SET_STATE",
					payload: {
						submissionData: parsedData,
						data: submission, // Full submission object with joined data (equipment_area, asset_type, etc.)
						formName: fName,
						formLogo: fLogo,
						address: addr,
						dateOfVisit: dateValue,
						locationId: locId,
						isLoading: false,
						isDeleted,
						isSubmissionCompleted: isCompleted,
					},
				});
			} catch (error) {
				console.error("Error fetching submission data:", error);
				message.error("Failed to load submission data.");
				dispatch({ type: "SET_LOADING", payload: false });
			}
		};

		fetchSubmission();
	}, [submissionId, user?.organization.config.logo_url]);

	// Special logo conversion with better quality preservation
	const convertLogoToDataUrl = async (url: string): Promise<string> => {
		try {
			return await new Promise<string>((resolve, reject) => {
				const img = document.createElement("img") as HTMLImageElement;
				img.crossOrigin = "anonymous";

				img.onload = () => {
					try {
						const canvas = document.createElement("canvas");
						const ctx = canvas.getContext("2d");

						if (!ctx) {
							throw new Error("Could not get canvas context");
						}

						// For logos, maintain aspect ratio and use reasonable dimensions
						const maxWidth = 240; // Higher resolution for logos
						const maxHeight = 80;

						let { width, height } = img;
						const aspectRatio = width / height;

						// Scale to fit within bounds while maintaining aspect ratio
						if (width > maxWidth) {
							width = maxWidth;
							height = maxWidth / aspectRatio;
						}
						if (height > maxHeight) {
							height = maxHeight;
							width = maxHeight * aspectRatio;
						}

						canvas.width = width;
						canvas.height = height;

						// Use white background for logos to handle transparency
						ctx.fillStyle = "white";
						ctx.fillRect(0, 0, width, height);
						ctx.drawImage(img, 0, 0, width, height);

						// Use higher quality for logos
						const dataUrl = canvas.toDataURL("image/png", 1.0);
						resolve(dataUrl);
					} catch (canvasError) {
						reject(canvasError);
					}
				};

				img.onerror = (imgError: any) => {
					reject(new Error("Logo failed to load"));
				};

				// Set a timeout
				setTimeout(() => {
					reject(new Error("Logo load timeout"));
				}, 10000);

				img.src = url;
			});
		} catch (error) {
			// Fallback to regular conversion
			return await convertImageToDataUrl(url);
		}
	};

	// Convert image URL to data URL with multiple fallback methods
	const convertImageToDataUrl = async (url: string): Promise<string> => {
		// Method 1: Try direct fetch
		try {
			const response = await fetch(url, {
				mode: "cors",
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const blob = await response.blob();

			return new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => {
					const result = reader.result as string;
					resolve(result);
				};
				reader.onerror = () => {
					reject(new Error("FileReader failed"));
				};
				reader.readAsDataURL(blob);
			});
		} catch (fetchError) {
			// Method 2: Try using an Image element to load and convert to canvas
			try {
				return await new Promise<string>((resolve, reject) => {
					const img = document.createElement("img") as HTMLImageElement;
					img.crossOrigin = "anonymous";

					img.onload = () => {
						try {
							const canvas = document.createElement("canvas");
							const ctx = canvas.getContext("2d");

							if (!ctx) {
								throw new Error("Could not get canvas context");
							}

							// Resize large images to prevent PDF generation issues
							const maxWidth = 800;
							const maxHeight = 600;

							let { width, height } = img;

							// Calculate new dimensions while maintaining aspect ratio
							if (width > maxWidth || height > maxHeight) {
								const aspectRatio = width / height;
								if (width > height) {
									width = maxWidth;
									height = maxWidth / aspectRatio;
								} else {
									height = maxHeight;
									width = maxHeight * aspectRatio;
								}
							}

							canvas.width = width;
							canvas.height = height;
							ctx.drawImage(img, 0, 0, width, height);

							// Use lower quality to reduce file size
							const dataUrl = canvas.toDataURL("image/jpeg", 0.5);

							// Check if the data URL is too large (over 2MB)
							if (dataUrl.length > 2 * 1024 * 1024) {
								const smallerDataUrl = canvas.toDataURL("image/jpeg", 0.3);
								if (smallerDataUrl.length > 2 * 1024 * 1024) {
									throw new Error(
										"Image still too large even at lowest quality",
									);
								}
								resolve(smallerDataUrl);
							} else {
								resolve(dataUrl);
							}
						} catch (canvasError) {
							reject(canvasError);
						}
					};

					img.onerror = (imgError: any) => {
						reject(new Error("Image failed to load"));
					};

					// Set a timeout
					setTimeout(() => {
						reject(new Error("Image load timeout"));
					}, 10000);

					img.src = url;
				});
			} catch (canvasError) {
				throw new Error(`All conversion methods failed for ${url}`);
			}
		}
	};

	// Generate PDF using @react-pdf/renderer (similar to leave behind reports)
	const generateSubmissionPDF = async (
		submissionData: any,
		formName: string,
		address: string,
		formLogo?: string,
	) => {
		const styles = StyleSheet.create({
			page: {
				flexDirection: "column",
				backgroundColor: "#ffffff",
				padding: 30,
				fontSize: 10,
				fontFamily: "Helvetica",
			},
			header: {
				marginBottom: 20,
				alignItems: "center",
			},
			logo: {
				width: 120,
				height: 40,
				marginBottom: 15,
				objectFit: "contain",
				alignSelf: "center",
			},
			title: {
				fontSize: 18,
				fontWeight: "bold",
				textAlign: "center",
				marginBottom: 10,
			},
			sectionTitle: {
				fontSize: 14,
				fontWeight: "bold",
				marginTop: 15,
				marginBottom: 8,
				backgroundColor: "#f0f0f0",
				padding: 5,
			},
			row: {
				flexDirection: "row",
				marginBottom: 5,
				minHeight: 20,
			},
			label: {
				width: "40%",
				fontWeight: "bold",
				paddingRight: 10,
			},
			value: {
				width: "60%",
				flexWrap: "wrap",
				fontSize: 10,
				lineHeight: 1.4,
			},
			image: {
				maxWidth: 200,
				maxHeight: 150,
				marginBottom: 5,
			},
		});

		const sections = submissionData?.sections || [];

		const renderSections = async () => {
			const elements: any[] = [];

			for (let sIndex = 0; sIndex < sections.length; sIndex++) {
				const section = sections[sIndex];
				if (!section) continue;

				const fields = section.questions || section.fields || [];
				const sectionHasValue = fields.some((q: any) => {
					if (!q) return false;
					const v = q.value;
					if (!v) return false;
					if (Array.isArray(v) && v.length === 0) return false;
					return true;
				});

				if (!sectionHasValue) continue;

				// Add section title
				elements.push(
					<Text key={`section-${sIndex}`} style={styles.sectionTitle}>
						{section.title}
					</Text>,
				);

				// Add fields
				for (let qIndex = 0; qIndex < fields.length; qIndex++) {
					const q = fields[qIndex];
					if (!q || !q.value) continue;

					const value = q.value;
					if (Array.isArray(value) && value.length === 0) continue;

					const label =
						q.label || q.fields?.[0]?.label || q.name || `Question #${qIndex}`;

					// Handle different field types
					if (q.type === "file" || q.type === "image") {
						// Extract URLs from various possible formats
						let imageUrls: string[] = [];

						if (Array.isArray(value)) {
							// Handle array of images
							imageUrls = value
								.map((item: any) => {
									// Handle different formats: string URL, object with url property, etc.
									if (typeof item === "string") return item;
									if (item && typeof item === "object") {
										return item.url || item.src || item.href || null;
									}
									return null;
								})
								.filter(
									(url: any) =>
										url && typeof url === "string" && isValidUrl(url),
								);
						} else if (value) {
							// Handle single image
							let url = null;
							if (typeof value === "string") {
								url = value;
							} else if (typeof value === "object") {
								url = value.url || value.src || value.href;
							}

							if (url && typeof url === "string" && isValidUrl(url)) {
								imageUrls = [url];
							}
						}

						// Convert images to data URLs and add them
						for (let imgIndex = 0; imgIndex < imageUrls.length; imgIndex++) {
							const url = imageUrls[imgIndex];

							try {
								const dataUrl = await convertImageToDataUrl(url);

								// Validate the data URL
								if (!dataUrl.startsWith("data:image/")) {
									throw new Error("Invalid data URL format");
								}

								elements.push(
									<View
										key={`${sIndex}-${qIndex}-img-${imgIndex}`}
										style={styles.row}
									>
										<Text style={styles.label}>
											{imageUrls.length > 1
												? `${label} ${imgIndex + 1}:`
												: `${label}:`}
										</Text>
										<PDFImage src={dataUrl} style={styles.image} />
									</View>,
								);
							} catch (error) {
								// Add a fallback text with the URL
								elements.push(
									<View
										key={`${sIndex}-${qIndex}-img-${imgIndex}`}
										style={styles.row}
									>
										<Text style={styles.label}>
											{imageUrls.length > 1
												? `${label} ${imgIndex + 1}:`
												: `${label}:`}
										</Text>
										<Text style={styles.value}>Image: {url}</Text>
									</View>,
								);
							}
						}
					} else {
						// Handle text values, arrays, and objects
						let displayValue = "";

						if (Array.isArray(value)) {
							// Handle arrays - format objects properly with better structure
							const formattedItems = value.map((item, index) => {
								if (typeof item === "object" && item !== null) {
									// For objects, try to extract meaningful properties
									if (item.name) return `${index + 1}. ${item.name}`;
									if (item.label) return `${index + 1}. ${item.label}`;
									if (item.title) return `${index + 1}. ${item.title}`;
									if (item.value) return `${index + 1}. ${item.value}`;
									if (item.text) return `${index + 1}. ${item.text}`;

									// For equipment-like objects, format as structured entries
									const entries = Object.entries(item)
										.filter(
											([key, val]) =>
												val !== null && val !== undefined && val !== "",
										)
										.map(([key, val]) => {
											// Capitalize and format key names
											const formattedKey = key
												.replace(/([A-Z])/g, " $1")
												.replace(/^./, (str) => str.toUpperCase())
												.trim();
											return `${formattedKey}: ${val}`;
										});

									if (entries.length > 0) {
										// If this looks like equipment data, format it nicely
										if (
											entries.some(
												(entry) =>
													entry.toLowerCase().includes("asset") ||
													entry.toLowerCase().includes("model") ||
													entry.toLowerCase().includes("serial"),
											)
										) {
											return `\n--- Equipment ${index + 1} ---\n${entries.join("\n")}`;
										} else {
											return `${index + 1}. ${entries.join(", ")}`;
										}
									}
									return `${index + 1}. [Empty Object]`;
								}
								return `${index + 1}. ${String(item)}`;
							});

							// Join with appropriate separators based on content
							if (
								formattedItems.some((item) => item.includes("--- Equipment"))
							) {
								displayValue = formattedItems.join("\n\n");
							} else {
								displayValue = formattedItems.join("\n");
							}
						} else if (typeof value === "object" && value !== null) {
							// Handle single objects
							if (value.name) displayValue = value.name;
							else if (value.label) displayValue = value.label;
							else if (value.title) displayValue = value.title;
							else if (value.value) displayValue = value.value;
							else if (value.text) displayValue = value.text;
							else {
								// Show key-value pairs for objects
								const entries = Object.entries(value)
									.filter(
										([key, val]) =>
											val !== null && val !== undefined && val !== "",
									)
									.map(([key, val]) => `${key}: ${val}`);
								displayValue =
									entries.length > 0 ? entries.join(", ") : "[Empty Object]";
							}
						} else {
							// Handle primitive values (string, number, boolean)
							displayValue = String(value);
						}

						if (displayValue.trim() && displayValue !== "[object Object]") {
							// Check if this is structured equipment data that should be broken into multiple rows
							if (
								Array.isArray(value) &&
								value.length > 0 &&
								typeof value[0] === "object" &&
								value[0] !== null &&
								(value[0].assetType || value[0].model || value[0].serialNumber)
							) {
								// Render equipment as separate structured entries
								elements.push(
									<Text
										key={`${sIndex}-${qIndex}-header`}
										style={styles.sectionTitle}
									>
										{label}
									</Text>,
								);

								value.forEach((item: any, itemIndex: number) => {
									if (typeof item === "object" && item !== null) {
										const entries = Object.entries(item)
											.filter(
												([key, val]) =>
													val !== null && val !== undefined && val !== "",
											)
											.map(([key, val]) => {
												const formattedKey = key
													.replace(/([A-Z])/g, " $1")
													.replace(/^./, (str) => str.toUpperCase())
													.trim();
												return { key: formattedKey, value: String(val) };
											});

										if (entries.length > 0) {
											// Add equipment number header
											elements.push(
												<View
													key={`${sIndex}-${qIndex}-item-${itemIndex}-header`}
													style={styles.row}
												>
													<Text
														style={[
															styles.label,
															{ fontWeight: "bold", color: "#333" },
														]}
													>
														Equipment #{itemIndex + 1}:
													</Text>
													<Text style={styles.value}></Text>
												</View>,
											);

											// Add each property as a separate row
											entries.forEach(({ key, value: val }, entryIndex) => {
												elements.push(
													<View
														key={`${sIndex}-${qIndex}-item-${itemIndex}-${entryIndex}`}
														style={styles.row}
													>
														<Text
															style={[
																styles.label,
																{ paddingLeft: 20, fontSize: 9 },
															]}
														>
															{key}:
														</Text>
														<Text style={[styles.value, { fontSize: 9 }]}>
															{val}
														</Text>
													</View>,
												);
											});

											// Add spacing between equipment items
											if (itemIndex < value.length - 1) {
												elements.push(
													<View
														key={`${sIndex}-${qIndex}-spacer-${itemIndex}`}
														style={{ height: 8 }}
													/>,
												);
											}
										}
									}
								});
							} else {
								// Regular single-value display
								elements.push(
									<View key={`${sIndex}-${qIndex}`} style={styles.row}>
										<Text style={styles.label}>{label}:</Text>
										<Text style={styles.value}>{displayValue}</Text>
									</View>,
								);
							}
						}
					}
				}
			}

			return elements;
		};

		// Convert logo to data URL if it exists with special handling for logos
		let logoDataUrl = null;
		if (formLogo) {
			try {
				// Use a special conversion for logos to maintain quality
				logoDataUrl = await convertLogoToDataUrl(formLogo);
			} catch (error) {
				// Logo conversion failed, continue without logo
			}
		}

		const renderedSections = await renderSections();

		const document = (
			<Document>
				<Page size="A4" style={styles.page}>
					<View style={styles.header}>
						{logoDataUrl && <PDFImage src={logoDataUrl} style={styles.logo} />}
						<Text style={styles.title}>{formName}</Text>
						{address && <Text>{address}</Text>}
					</View>

					{renderedSections}
				</Page>
			</Document>
		);

		try {
			return await pdf(document).toBlob();
		} catch (pdfError) {
			// If PDF generation fails, try without images

			const documentWithoutImages = (
				<Document>
					<Page size="A4" style={styles.page}>
						<View style={styles.header}>
							<Text style={styles.title}>{formName}</Text>
							{address && <Text>{address}</Text>}
						</View>

						<View>
							<Text style={{ color: "red", marginBottom: 10 }}>
								Note: Images could not be included in this PDF due to size
								limitations.
							</Text>
						</View>

						{/* Render sections without images */}
						{submissionData.sections?.map((section: any, sIndex: number) => {
							if (!section.fields || section.fields.length === 0) return null;

							return (
								<View key={sIndex}>
									<Text style={styles.sectionTitle}>{section.name}</Text>
									{section.fields.map((field: any, fIndex: number) => {
										const q = field.question || field;
										if (!q || !q.value || q.value === "" || q.value === "N/A")
											return null;

										// Skip image fields for this fallback
										if (
											typeof q.value === "string" &&
											(q.value.includes("firebasestorage.googleapis.com") ||
												q.value.includes("http") ||
												q.value.includes(".jpg") ||
												q.value.includes(".png") ||
												q.value.includes(".jpeg") ||
												q.value.includes(".webp"))
										) {
											return (
												<View key={`${sIndex}-${fIndex}`} style={styles.row}>
													<Text style={styles.label}>
														{q.label || q.question || "Image"}:
													</Text>
													<Text style={styles.value}>
														[Image omitted due to size]
													</Text>
												</View>
											);
										}

										return (
											<View key={`${sIndex}-${fIndex}`} style={styles.row}>
												<Text style={styles.label}>
													{q.label || q.question}:
												</Text>
												<Text style={styles.value}>{String(q.value)}</Text>
											</View>
										);
									})}
								</View>
							);
						})}
					</Page>
				</Document>
			);

			return await pdf(documentWithoutImages).toBlob();
		}
	};

	// Count images in submission data
	const countImagesInSubmission = (submissionData: any): number => {
		let imageCount = 0;
		const sections = submissionData?.sections || [];

		sections.forEach((section: any) => {
			const fields = section.questions || section.fields || [];
			fields.forEach((q: any) => {
				if ((q.type === "file" || q.type === "image") && q.value) {
					if (Array.isArray(q.value)) {
						imageCount += q.value.length;
					} else {
						imageCount += 1;
					}
				}
			});
		});

		return imageCount;
	};

	// Export to PDF by capturing the page as-is with all images
	const downloadPdfWithHtml2Pdf = async () => {
		const element = document.getElementById("submission-content");
		if (!element) {
			throw new Error("Could not find #submission-content element.");
		}

		// Wait for all images to load
		const images = element.querySelectorAll("img");
		await Promise.all(
			Array.from(images).map(
				(img) =>
					new Promise((resolve) => {
						if (img.complete) {
							resolve(true);
						} else {
							img.onload = () => resolve(true);
							img.onerror = () => resolve(true);
						}
					}),
			),
		);

		// Wait for any pending renders
		await new Promise((resolve) => setTimeout(resolve, 200));

		// Optimized options for capturing the page as-is with all images
		const opt = {
			margin: [0.5, 0.5, 0.5, 0.5],
			filename: `${formName || "Submission"} - ${address || ""}.pdf`,
			image: { type: "jpeg", quality: 0.95 }, // Higher quality for better image rendering
			html2canvas: {
				scale: 2, // Higher scale for better quality
				useCORS: true,
				allowTaint: true, // Allow tainted canvas for cross-origin images
				backgroundColor: "#ffffff",
				logging: false,
				removeContainer: true,
				foreignObjectRendering: false,
				imageTimeout: 15000, // Give images more time to load
				ignoreElements: (elem: Element) => {
					const className = elem.getAttribute("class") || "";
					const tagName = elem.tagName.toLowerCase();

					// Hide elements that shouldn't be in PDF
					if (className.includes("submission-edit-log")) return true;
					if (className.includes("ant-typography-copy")) return true;
					if (className.includes("ant-btn")) return true;
					if (className.includes("ant-image-mask")) return true; // Hide image preview overlay
					if (tagName === "button") return true;

					return false;
				},
			},
			jsPDF: {
				unit: "in",
				format: "letter",
				orientation: "portrait",
				compress: true,
			},
			pagebreak: {
				mode: ["css", "legacy"],
				before: ".page-break-before",
				after: ".page-break-after",
				avoid: ["img", ".ant-image"], // Avoid breaking images
			},
		};

		const pdf = html2pdf().from(element).set(opt);

		await pdf.save();
	};

	// Download PDF - using html2pdf to capture the page as-is with all images
	const downloadPdf = async () => {
		dispatch({ type: "SET_STATE", payload: { pdfLoading: true } });

		try {
			await downloadPdfWithHtml2Pdf();
			dispatch({ type: "SET_STATE", payload: { pdfLoading: false } });
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error occurred";
			message.error(`Failed to generate PDF: ${errorMessage}`);
			dispatch({ type: "SET_STATE", payload: { pdfLoading: false } });
		}
	};

	// Export to CSV
	const exportToCSV = async () => {
		try {
			const submissionId = state.data?.id;
			const blob = await exportSubmissionToCSV(submissionId);
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${formName || "Submission"} - ${address || ""}.csv`;
			link.click();
		} catch (err) {
			console.error("Error exporting CSV:", err);
			message.error("Failed to export to CSV.");
		}
	};

	// If loading
	if (isLoading) {
		return (
			<div className="h-screen w-[100vw-150px] flex flex-col justify-center items-center">
				<Spin tip="Loading submission data..." size="large" />
			</div>
		);
	}

	// Callback to refresh data after approval
	const handleRefresh = () => {
		// Re-fetch the submission data
		dispatch({ type: "SET_LOADING", payload: true });
		setTimeout(() => {
			dispatch({ type: "SET_LOADING", payload: false });
		}, 500);
	};

	return (
		<div className="h-100">
			<Card className="p-3 submission-wrapper" id="submission">
				<SubmissionDetailsHeader
					isQuickView={quickView}
					toggleSubmission={toggleSubmission}
					setModalIndex={setModalIndex}
					responsesLength={responsesLength}
					dispatch={dispatch}
					state={state}
					exportToCSV={exportToCSV}
					downloadPdf={downloadPdf}
					organization={user?.organization}
					onRefresh={handleRefresh}
				/>
				<DynamicSubmissionDetailsContent
					state={state}
					userOrgId={user?.organization.external_firebase_id || ""}
					assetTypesMap={assetTypesMap}
					equipmentAreasMap={equipmentAreasMap}
				/>
			</Card>
		</div>
	);
};
